import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Category, isProjectScope, isReleaseGroupScope } from '../../../../../shared/issuesV2';
import { MAX_CUSTOM_SEVERITY_REASON_LENGTH } from '../../../../../shared/model-typings/customseverity';
import nuc from '../../../../../shared/utils/nuc';
import { MAX_CUSTOM_SEVERITY, MIN_CUSTOM_SEVERITY } from '../../../../../shared/vulns/severity';
import { BannerAttention, BannerStyle } from '../../../../../typings/enums';
import { useDresser } from '../../../../contexts/DrawerContext';
import useCreateCustomRiskScore from '../../../../hooks/api/customRiskScores/useCreateCustomRiskScore';
import useDeleteCustomRiskScore from '../../../../hooks/api/customRiskScores/useDeleteCustomRiskScore';
import useUpdateCustomRiskScore from '../../../../hooks/api/customRiskScores/useUpdateCustomRiskScore';
import useEditable from '../../../../hooks/useEditable';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import Banner from '../../../generic/Banner';
import Button, { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup } from '../../../generic/Button';
import CustomRiskScoreBadge from '../../../generic/CustomRiskScoreBadge';
import Flex from '../../../generic/Flex';
import CharacterCount from '../../../generic/Forms/CharacterCount';
import FormGroup from '../../../generic/Forms/FormGroup';
import LabeledNumberInput from '../../../generic/Forms/LabeledNumberInput';
import LabeledTextArea from '../../../generic/Forms/LabeledTextArea';
import Header from '../IssueDetailsV2/Header';
import Status from '../Status';
import type { VulnIssue } from '../../../../../shared/issuesV2';
import type { CustomSeverityScope } from '../../../../../shared/model-typings/customseverity';

import styles from './CustomRiskScoreDrawer.module.less';

const SCORE_RANGES = [
  { range: '90 - 100', score: 90 },
  { range: '70 - 89', score: 70 },
  { range: '40 - 69', score: 40 },
  { range: '0 - 39', score: 0 },
];

type FormData = {
  score: number | null;
  reason: string;
};

type Props = {
  issue: VulnIssue;
};

const CustomRiskScoreDrawer: VoidFunctionComponent<Props> = ({ issue }) => {
  const dispatch = useDispatch();
  const { popDrawer, clearDrawers } = useDresser();
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const existingScore = issue.customRiskScore;
  const isEditing = !!existingScore;

  const initialState = useMemo((): FormData => ({
    score: existingScore ? existingScore.score : null,
    reason: existingScore?.reason ?? '',
  }), [existingScore]);

  const { data, isDirty, onUpdate } = useEditable<FormData>(initialState);

  const createMutation = useCreateCustomRiskScore();
  const updateMutation = useUpdateCustomRiskScore();
  const { onDeleteCustomRiskScore, isDeleting } = useDeleteCustomRiskScore();

  const isMutating = createMutation.isPending || updateMutation.isPending || isDeleting;
  const isSaveDisabled = !isDirty || isMutating || data.score === null;

  const scopeParams = useMemo((): { type: CustomSeverityScope; id: string } | null => {
    if (isProjectScope(scope)) {
      return { type: 'project', id: nuc(scope.id) };
    }
    if (isReleaseGroupScope(scope)) {
      return { type: 'release_group', id: scope.id };
    }
    return null;
  }, [scope]);

  const onMutationSuccess = useCallback(() => {
    // Refresh the issue details (issue page)
    dispatch(IssuesV2Store.actions.refetchIssueDetailsAfterAction(issue.id));
    // Refresh the issues table summaries (issues list)
    dispatch(IssuesV2Store.actions.refetchSummaries());
    clearDrawers();
  }, [dispatch, issue.id, clearDrawers]);

  const handleSubmit = useCallback(() => {
    if (!isDirty || !scopeParams || data.score === null) {
      return;
    }

    const trimmedReason = data.reason.trim();
    const mutationOptions = { onSuccess: onMutationSuccess };

    if (isEditing) {
      updateMutation.mutate({
        issueId: issue.id,
        body: { score: data.score, reason: trimmedReason || null },
        scope: scopeParams,
      }, mutationOptions);
    } else {
      createMutation.mutate({
        issueId: issue.id,
        body: { score: data.score, reason: trimmedReason || undefined },
        scope: scopeParams,
      }, mutationOptions);
    }
  }, [
    isDirty, scopeParams, data, isEditing, updateMutation,
    createMutation, issue.id, onMutationSuccess,
  ]);

  const handleScoreChange = useCallback((value: string) => {
    if (value === '') {
      onUpdate({ score: null });
      return;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }
    const integer = Math.trunc(parsed);
    const clamped = Math.min(MAX_CUSTOM_SEVERITY, Math.max(MIN_CUSTOM_SEVERITY, integer));
    onUpdate({ score: clamped });
  }, [onUpdate]);

  const handleDelete = useCallback(() => {
    if (!scopeParams) {
      return;
    }

    onDeleteCustomRiskScore(
      { issueId: issue.id, scope: scopeParams },
      { onSuccess: onMutationSuccess },
    );
  }, [scopeParams, onDeleteCustomRiskScore, issue.id, onMutationSuccess]);

  // Skip Global scope
  if (!scopeParams) {
    return null;
  }

  return (
    <div className={styles.customRiskScoreDrawer}>
      <Header category={Category.Vulnerability} />
      <Status />
      <h4>
        {isEditing ? 'Edit Custom Risk Score' : 'Assign Custom Risk Score'}
      </h4>
      <p>
        Define how risky this vulnerability is for your project, based on your internal context.
      </p>
      <FormGroup spacing="md">
        <LabeledNumberInput
          label="Score"
          value={(data.score ?? '') as number}
          min={MIN_CUSTOM_SEVERITY}
          max={MAX_CUSTOM_SEVERITY}
          onChange={handleScoreChange}
          placeholder="Enter a number between 0-100"
          step={1}
          textAlign="left"
          isRequired
            />
        <Flex direction="column" align="flex-start" gap="8px" className={styles.scoresListContainer}>
          {SCORE_RANGES.map(({ range, score }) => (
            <Flex key={range} direction="row" align="center" justify="space-between" className={styles.scoreItem}>
              <div className={styles.scoreRangeLabel}>{range}</div>
              <CustomRiskScoreBadge score={score} hasTooltip={false} showScore={false} />
            </Flex>
          ))}
        </Flex>
        <LabeledTextArea
          label="Reason (optional)"
          value={data.reason}
          onChange={(value: string) => onUpdate({ reason: value })}
          maxLength={MAX_CUSTOM_SEVERITY_REASON_LENGTH}
          placeholder="Let others know why you changed this severity score"
              />
        <CharacterCount
          maxLength={MAX_CUSTOM_SEVERITY_REASON_LENGTH}
          currentLength={data.reason.length}
              />
      </FormGroup>

      <Banner
        title="The custom risk score will become the primary severity in the issues table"
        body="When set, the custom risk score is shown as the primary severity in the issues table. The original CVSS score remains available in the issue details for reference, and the custom risk score can be edited or removed at any time."
        bannerStyle={BannerStyle.Low}
        bannerAttention={BannerAttention.Low} />

      <Flex className="margin-top-md">
        {isEditing && (
          <Button
            type={BUTTON_TYPE.ERROR}
            size={BUTTON_SIZE.MEDIUM}
            onClick={handleDelete}
            isDisabled={isMutating}
              >
            Remove Custom Score
          </Button>
        )}
        <div className="margin-left-auto">
          <ButtonGroup spacing="xs">
            <Button
              type="secondary"
              size={BUTTON_SIZE.MEDIUM}
              onClick={popDrawer}
              isDisabled={isMutating}
                >
              Cancel
            </Button>

            <Button
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={handleSubmit}
              isDisabled={isSaveDisabled}
                >
              Save
            </Button>
          </ButtonGroup>
        </div>
      </Flex>
    </div>
  );
};

export default CustomRiskScoreDrawer;
